"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlCaplessLineGraphicsComponent = void 0;
const tslib_1 = require("tslib");
const rc_js_util_1 = require("rc-js-util");
const i_gl_indexed_point2d_binder_1 = require("../indexed-point-2d/i-gl-indexed-point2d-binder");
const core_1 = require("@visualization-tools/core");
/**
 * @public
 * Draws lines without caps, without generating any intermediate geometry. Caps can be added by combining with a cap drawing component.
 * Handles sizes and colors per point.
 */
class GlCaplessLineGraphicsComponent {
    constructor(cameraBinder, indexedBinder) {
        this.cameraBinder = cameraBinder;
        this.indexedBinder = indexedBinder;
        this.type = core_1.EGraphicsComponentType.Entity;
        DEBUG_MODE && rc_js_util_1._Debug.runBlock(() => {
            (0, core_1.assertBinder)(indexedBinder, i_gl_indexed_point2d_binder_1.IndexedPoint2dIdentifier);
            rc_js_util_1._Debug.assert(indexedBinder.pointsBound === 2, "requires 2 point to be bound");
        });
        this.specification = core_1.GlProgramSpecification.mergeProgramSpecifications([
            cameraBinder.specification,
            indexedBinder.specification,
            caplessProgramSpecification,
        ]);
        this.bindings = GlCaplessLineGraphicsComponent.getBindings();
        this.transform = core_1.GlTransformProvider.createOne(this, this.indexedBinder, (updateArg) => updateArg.userTransform, (entity) => entity);
    }
    getCacheId() {
        return [
            "CaplessLine",
            this.cameraBinder.getBinderId(),
            this.indexedBinder.getBinderId(),
        ].join("_");
    }
    getLinkableBinders() {
        return [this.indexedBinder];
    }
    initialize(componentRenderer) {
        this.cameraBinder.initialize(componentRenderer);
        this.indexedBinder.initialize(componentRenderer);
        this.bindings.trianglePositionAttribute.initialize(componentRenderer);
        this.bindings.canvasToClipUniform.initialize(componentRenderer);
    }
    onBeforeUpdate(componentRenderer, updateArg) {
        this.bindings.trianglePositionAttribute.bindArray(componentRenderer, componentRenderer.context.STATIC_DRAW);
        const frameId = componentRenderer.sharedState.frameCounter;
        this.bindings.canvasToClipUniform.setData(updateArg.canvasDimensions.pixelToClipSize, frameId);
        this.bindings.canvasToClipUniform.bind(componentRenderer);
    }
    update(entity, componentRenderer, updateArg) {
        if (entity.data.getLength() < 2) {
            // nothing to draw
            return;
        }
        this.cameraBinder.update(this.cameraBinder.getBinderData(entity, updateArg, componentRenderer), componentRenderer);
        this.indexedBinder.updateInstanced(entity, componentRenderer, 1);
        this.indexedBinder.overrideColors(componentRenderer, entity, entity.changeId);
        // draw odds
        componentRenderer.drawInstancedArrays(componentRenderer.context.TRIANGLE_STRIP, 0, 4, (entity.data.getLength() * 0.5) | 0);
        // draw evens
        this.indexedBinder.setPointers(entity.data.getStart() + 1, entity.data.getBlockByteSize());
        this.indexedBinder.bindInstanced(componentRenderer, 1);
        componentRenderer.drawInstancedArrays(componentRenderer.context.TRIANGLE_STRIP, 0, 4, ((entity.data.getLength() - 1) * 0.5) | 0);
    }
    static getBindings() {
        const trianglePosition = new core_1.GlFloatAttribute("caplessLine_trianglePosition", new core_1.GlFloatBuffer(new Float32Array([
            0, 0,
            1, 0,
            0, 1,
            1, 1,
        ])), 2);
        return {
            trianglePositionAttribute: trianglePosition,
            canvasToClipUniform: new core_1.GlMat3Uniform("caplessLine_canvasToClipSize", new rc_js_util_1.Mat3.f32()),
        };
    }
}
(0, tslib_1.__decorate)([
    rc_js_util_1.Once
], GlCaplessLineGraphicsComponent.prototype, "getCacheId", null);
exports.GlCaplessLineGraphicsComponent = GlCaplessLineGraphicsComponent;
// language=GLSL prefix="#if __VERSION__ >=300 && __VERSION__ < 400 \n #define ATTRIBUTE in \n #define VARYING out \n #else \n #define ATTRIBUTE attribute \n #define VARYING varying \n #endif"
const vertexShaderPartial = new core_1.GlShader(
// @formatter:off
`
/* === capless line === */
ATTRIBUTE lowp vec2 caplessLine_trianglePosition;
uniform highp mat3 caplessLine_canvasToClipSize;
VARYING lowp vec4 caplessLine_lineColor;

float caplessLine_getSize(vec2 sizes)
{
    return mix(sizes[0], sizes[1], caplessLine_trianglePosition.y);
}

vec2 caplessLine_getAdjustedPoistion(vec2 p1, vec2 p2, vec2 sizes)
{
    vec2 normal = generate2dLineNormal(p1, p2, vec2(1));
    vec2 position = mix(p1, p2, caplessLine_trianglePosition.y);

    float xSign = caplessLine_trianglePosition.x * 2. - 1.;
    vec2 pixelWidthOffset = xSign * caplessLine_getSize(sizes) * normal;

    return position + mat3MultiplyVec2(caplessLine_canvasToClipSize, pixelWidthOffset);
}

vec4 caplessLine_getColor(vec4 p1Color, vec4 p2Color)
{
    return mix(p1Color, p2Color, caplessLine_trianglePosition.y);
}

void main()
{
    vec3 p1 = v_2dCamera_getClipspaceCoords2d(pointConnector_getPosition());
    vec3 p2 = v_2dCamera_getClipspaceCoords2d(pointConnector_getPosition1());
    vec2 sizes = vec2(pointConnector_getSize(), pointConnector_getSize1()) * 0.5;

    gl_Position = vec4(caplessLine_getAdjustedPoistion(p1.xy, p2.xy, sizes), p1.z, 1);
    
    vec4 p1Color = pointConnector_getColor();
    vec4 p2Color = pointConnector_getColor1();
    caplessLine_lineColor = caplessLine_getColor(p1Color, p2Color);
}
`);
// language=GLSL prefix="#if __VERSION__ >=300 && __VERSION__ < 400 \n #define VARYING in \n #define TEXTURE2D texture \n #else \n #define VARYING varying \n #define TEXTURE2D texture2D \n #endif \n void setFragmentColor(in lowp vec4 color);"
const fragmentShaderSource = new core_1.GlShader(
// @formatter:off
`
VARYING lowp vec4 caplessLine_lineColor;

void main()
{
    setFragmentColor(caplessLine_lineColor);
}
`);
const caplessProgramSpecification = new core_1.GlProgramSpecification(core_1.GlShader.combineShaders([
    core_1.generate2LinedNormalShader,
    core_1.mat3MultiplyVec2Shader,
    vertexShaderPartial,
]), fragmentShaderSource, ["ANGLE_instanced_arrays"]);
//# sourceMappingURL=gl-capless-line-graphics-component.js.map